import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import { memo, useContext, useMemo, useRef, useState } from 'react';
import Barchart from 'components/molecules/Barchart';
import HorizontalInfiniteScroller from 'components/molecules/HorizontalInfiniteScroller';
import Loader from 'components/molecules/Loader';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import {
    calculateContainerHeight,
    formatNumWithKMBAlt,
    getConfig,
    scaleMaxValue,
    scaleMinValue,
} from 'utils/helperMethods';
import useDecompositionWaterfallData from './hooks/useDecompositionWaterfallData';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import { isEmpty } from 'lodash';
import WaterFallChartIndividualised from 'components/molecules/WaterFallChartIndividualised';
import { UserContext } from 'contexts/ConfigContext';

const MemoisedWaterFallChartIndividualised = memo(
    ({
        _datum,
        chartHeight,
        currentGraphWidth,
        minBound,
        maxBound,
        defaultCurrencySymbol,
    }: {
        _datum: any;
        chartHeight: number;
        currentGraphWidth: number;
        minBound: number;
        maxBound: number;
        defaultCurrencySymbol: string;
    }) => {
        return (
            <div className="hover:bg-black/5 z-10" key={`decomposition-waterfall-chart-${_datum.attributeName}`}>
                <WaterFallChartIndividualised
                    containerId={`decomposition-waterfall-chart-${_datum.attributeName}`}
                    chartWidth={currentGraphWidth}
                    chartHeight={chartHeight}
                    key={`decomposition-waterfall-chart-${_datum.attributeName}`}
                    dataSet={[_datum]}
                    xAxisField="attributeName"
                    yAxisField="kpiValue"
                    colorField="trend"
                    minBound={minBound}
                    maxBound={scaleMaxValue(20, maxBound)}
                    toolTipLabel={'Mix Impact'}
                    tooltipFormatter={(xAxis, val) => {
                        return {
                            name: 'Mix Impact',
                            value: `${defaultCurrencySymbol}${formatNumWithKMBAlt(val)}`,
                        };
                    }}
                    intervalFormatter={(val) => {
                        return `${defaultCurrencySymbol}${formatNumWithKMBAlt(val)}`;
                    }}
                />
                <p className="text-center">{_datum.attributeName}</p>
            </div>
        );
    },
);

const DecompositionWaterfallChart = () => {
    const chartRef = useRef(null);
    const chartHeight = calculateContainerHeight(2.3);
    const { appConfig } = useContext(UserContext);
    const profitPoolConfig = getConfig('profitPool', appConfig);
    const { defaultCurrencySymbol } = profitPoolConfig;

    const [toLeft] = useState(0);

    const containerWidth = useContainerWidth({
        containerId: 'decomposition-waterfall-infinite-scroll',
        buffer: 300,
    });

    const { waterFallChartData, totalMix, min: waterfallMin, max: waterfallMax } = useDecompositionWaterfallData();

    const minBound = useMemo(() => {
        return waterfallMin > 0 ? 0 : scaleMinValue(10, waterfallMin);
    }, [waterfallMin]);
    const maxBound = useMemo(() => {
        return waterfallMax > 0 ? waterfallMax : 0;
    }, [waterfallMax]);

    const minGraphWidth = 100;
    const currentDataLength = waterFallChartData?.length ?? 0;
    const currentContainerWidth = containerWidth;

    const currentGraphWidth =
        currentContainerWidth / currentDataLength < minGraphWidth
            ? minGraphWidth
            : currentContainerWidth / currentDataLength;

    return (
        <div>
            <div className="items-center flex flex-row justify-between my-2 mb-4">
                <div className="text-sm">
                    <p>{'Profit Growth'}</p>
                </div>
                <div className="flex flex-row gap-2 text-xs">
                    <div className="w-3 h-2 self-center bg-[#5F49BC]" />
                    <p>{'Total'}</p>
                    <div className="w-3 h-2 self-center bg-[#42A531]" />
                    <p>{'Positive'}</p>
                    <div className="w-3 h-2 self-center bg-[#CA3D3D]" />
                    <p>{'Negative'}</p>
                </div>
                <div />
            </div>

            {/* Chart */}
            <div className="flex mt-2 flex-row">
                {/* Y Axis */}
                <div className="flex flex-col">
                    <Barchart
                        padding={[0, 0, -2, 0]}
                        barWidth={40}
                        xAxisConfig={false}
                        xAxisLabelFormatter={(val) => {
                            return '';
                        }}
                        dataset={[]}
                        height={chartHeight}
                        loading={false}
                        chartContainer={`decomposition-waterfall-y-axis`}
                        fields={['brand_name', 'totalProfit', 'type']}
                        minBound={minBound}
                        maxBound={scaleMaxValue(20, maxBound)}
                        width={50}
                        colorKey={'type'}
                        yAxisConfig={true}
                        YGrid={false}
                        yAxisLabelFormatter={(val: any) => {
                            return `${defaultCurrencySymbol}${formatNumWithKMBAlt(val)}`;
                        }}
                    />
                </div>

                <div
                    id={`decomposition-waterfall-infinite-scroll`}
                    className={`flex-1 flex overflow-x-auto max-w-full z-10`}
                    ref={chartRef}
                >
                    {waterFallChartData?.map((_datum: any) => {
                        return (
                            <MemoisedWaterFallChartIndividualised
                                _datum={_datum}
                                chartHeight={chartHeight}
                                currentGraphWidth={currentGraphWidth}
                                minBound={minBound}
                                maxBound={maxBound}
                                defaultCurrencySymbol={defaultCurrencySymbol}
                            />
                        );
                    })}
                </div>

                {/* Current Year Chart */}
                <div className="flex flex-col">
                    <Barchart
                        padding={[0, 0, -8, 0]}
                        barWidth={40}
                        xAxisConfig={true}
                        xAxisLabelFormatter={(val) => {
                            return '';
                        }}
                        dataset={[
                            {
                                brand_name: 'Total Mix',
                                totalProfitGrowth: totalMix ?? 0,
                                type: '#5F49BF',
                            },
                        ]}
                        height={chartHeight}
                        loading={false}
                        chartContainer={`decomposition-waterfall-current-year`}
                        fields={['brand_name', 'totalProfitGrowth', 'type']}
                        minBound={minBound}
                        maxBound={scaleMaxValue(20, maxBound)}
                        width={100}
                        colorKey={'type'}
                        YGrid={true}
                        yAxisConfig={true}
                        tooltipFormatter={(xAxis, val) => {
                            return {
                                name: 'Total Mix',
                                value: `${defaultCurrencySymbol}${formatNumWithKMBAlt(val)}`,
                            };
                        }}
                        intervalFormatter={(val) => {
                            return `${defaultCurrencySymbol}${formatNumWithKMBAlt(val)}`;
                        }}
                    />
                    <p className="text-center">{'Current Year'}</p>
                </div>
            </div>
        </div>
    );
};

export default DecompositionWaterfallChart;
